/**
* Cv.ts
* @author Ram Thiru (ramthi) and Hector Hernandez (hectorh)
* @copyright Microsoft 2018
*/
import { LoggingSeverity, _ExtendedInternalMessageId, safeGetLogger } from "@microsoft/1ds-core-js";
var CvConstant = /** @class */ (function () {
    function CvConstant() {
    }
    return CvConstant;
}());
var Cv = /** @class */ (function () {
    function Cv(initValue, core) {
        this._base = "";
        this._currentElement = 0;
        this._base64CharSet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
        // cv version-dependent constants
        this._cv1Constants = new CvConstant();
        this._cv2Constants = new CvConstant();
        this._cvVersionAtLatestValidityCheck = 2;
        this._traceLogger = safeGetLogger(core);
        this._cv1Constants.maxCorrelationVectorLength = 63;
        this._cv1Constants.baseLength = 16;
        this._cv1Constants.validationPattern = new RegExp("^[" + this._base64CharSet + "]{" + this._cv1Constants.baseLength.toString() + "}(\.[0-9]+)*$");
        this._cv2Constants.maxCorrelationVectorLength = 127;
        this._cv2Constants.baseLength = 22;
        this._cv2Constants.validationPattern = new RegExp("^[" + this._base64CharSet + "]{" + this._cv2Constants.baseLength.toString() + "}(\.[0-9]+)*$");
        this._currentCvConstants = this._cv2Constants;
        this.init(initValue);
    }
    Cv.prototype.isInit = function () {
        return this.isValid(this.storedCv());
    };
    Cv.prototype.storedCv = function () {
        /// <summary> creates the cv element from stored variables </summary>
        /// <returns> cV element in string format </returns>
        return this._base.concat(".", this._currentElement.toString());
    };
    Cv.prototype.getValue = function () {
        /// <summary>
        /// Privileged method to serialize the current value of the Correlation Vector.
        /// CV should not be read from meta tag, but this is a breaking change.
        /// </summary>
        /// <returns type='string'>Serialized value of the Correlation Vector</returns>
        var value = this.storedCv();
        if (this.isValid(value)) {
            return value;
        }
    };
    Cv.prototype.getRelatedCv = function () {
        return this._relatedCv;
    };
    Cv.prototype.incrementExternal = function (externalCv) {
        /// <summary> this method verifies and increments external cVs </summary>
        /// <param name='externalCv' type='string'> the cv to be incremented </param>
        /// <returns type='string'> the incremented cv if the incrementation was succesful </returns>
        if (this.isValid(externalCv)) {
            // decompose external cV
            var externalCvParts = externalCv.split(".");
            var numberOfCvParts = externalCvParts.length;
            // increment last part of cV
            externalCvParts[numberOfCvParts - 1] = (parseInt(externalCvParts[numberOfCvParts - 1], 10) + 1).toString();
            // recombine cV
            var incrementedCv = "";
            for (var i = 0; i < numberOfCvParts; i++) {
                incrementedCv += (externalCvParts[i]);
                if (i < (numberOfCvParts - 1)) {
                    incrementedCv += ".";
                }
            }
            // to support cv 1 & 2
            var maxLength = (externalCvParts[0].length === this._cv2Constants.baseLength) ?
                this._cv2Constants.maxCorrelationVectorLength : this._cv1Constants.maxCorrelationVectorLength;
            if (incrementedCv.length <= maxLength) {
                return incrementedCv;
            }
        }
    };
    Cv.prototype.canExtend = function () {
        /// <summary>
        /// Private method to check if the Correlation Vector can be extended
        /// </summary>
        /// <param name='ref' type='reference'>Object reference to the Correlation Vector</param>
        /// <returns type='boolean'>True if the Correlation Vector can be extended, false otherwise.</returns>
        var currentCv = this.storedCv();
        if (this.isValid(currentCv)) {
            return this._isLeqThanMaxCorrelationVectorLength(currentCv.length + 2);
        }
        return false;
    };
    Cv.prototype.canIncrement = function () {
        /// <summary>
        /// Private method to check if the Correlation Vector can be incremented
        /// </summary>
        /// <returns type='boolean'>True if the Correlation Vector can be incremented, false otherwise.</returns>
        if (this.isValid(this.storedCv())) {
            return this._isLeqThanMaxCorrelationVectorLength(this._base.length + 1 + ((this._currentElement + 1) + "").length);
        }
        return false;
    };
    /**
    * @ Sets the base for the cv object
    * @ If the passed in value is a cv itself Base and digits
    * @param _base   - The base value of the cv.
    */
    Cv.prototype.setValue = function (_base) {
        if (this.isValid(_base)) {
            var lastIndex = _base.lastIndexOf(".");
            if (lastIndex > 1) {
                this._base = _base.substr(0, lastIndex);
                this._currentElement = parseInt(_base.substr(lastIndex + 1), 10);
            }
            else {
                this._base = _base;
                this._currentElement = 0;
            }
            return true;
        }
        else {
            this._traceLogger.throwInternal(LoggingSeverity.WARNING, _ExtendedInternalMessageId.InvalidCorrelationValue, "Cannot set invalid correlation vector value");
            return false;
        }
    };
    /**
    * Legacy method to support backward compatibility with JSLL shim do not use outside of shim
    * This method accepts cV that are already seeded and do not implement related cV logic
    */
    Cv.prototype.setValueLegacy = function (cv) {
        // Related cV functionality was removed from the SDK so legacy and regular would be the same
        this.setValue(cv);
        return this.getValue();
    };
    Cv.prototype.seed = function () {
        var result = "";
        for (var i = 0; i < this._currentCvConstants.baseLength; i++) {
            result += this._base64CharSet.charAt(Math.floor(Math.random() * this._base64CharSet.length));
        }
        return result;
    };
    Cv.prototype.extend = function () {
        if (this.canExtend()) {
            this._base = this._base.concat(".", this._currentElement.toString());
            this._currentElement = 0;
            return this.storedCv();
        }
    };
    Cv.prototype.increment = function () {
        if (this.canIncrement()) {
            this._currentElement = this._currentElement + 1;
            return this.storedCv();
        }
    };
    Cv.prototype.validateWithCv1 = function (_cv) {
        if (this._cv1Constants.validationPattern.test(_cv) && _cv.length <= this._cv1Constants.maxCorrelationVectorLength) {
            return true;
        }
    };
    Cv.prototype.validateWithCv2 = function (_cv) {
        if (this._cv2Constants.validationPattern.test(_cv) && _cv.length <= this._cv2Constants.maxCorrelationVectorLength) {
            return true;
        }
    };
    Cv.prototype.useCv1 = function () {
        this._currentCvConstants = this._cv1Constants;
    };
    Cv.prototype.useCv2 = function () {
        this._currentCvConstants = this._cv2Constants;
    };
    Cv.prototype.isValid = function (_cv) {
        if (_cv) {
            var baseValue = _cv.split(".")[0];
            if (baseValue) {
                if (baseValue.length === 16) {
                    this._cvVersionAtLatestValidityCheck = 1;
                    return this.validateWithCv1(_cv);
                }
                else if (baseValue.length === 22) {
                    this._cvVersionAtLatestValidityCheck = 2;
                    return this.validateWithCv2(_cv);
                }
            }
        }
    };
    Cv.prototype.init = function (cvInitValue) {
        if (cvInitValue) {
            this.setValue(cvInitValue);
        }
        else {
            this._base = this.seed();
            this._currentElement = 0;
        }
        return this.getValue();
    };
    /**
    * Legacy method to support backward compatibility with JSLL shim do not use outside of shim
    * This method accepts cV that are already seeded and do not implement related cV logic
    */
    Cv.prototype.initLegacy = function (cvInitValue) {
        // Related cV functionality was removed from the SDK so legacy and regular would be the same
        return this.init(cvInitValue);
    };
    Cv.prototype._isLeqThanMaxCorrelationVectorLength = function (length) {
        if (this._cvVersionAtLatestValidityCheck === 1) {
            return length <= this._cv1Constants.maxCorrelationVectorLength;
        }
        else {
            return length <= this._cv2Constants.maxCorrelationVectorLength;
        }
    };
    return Cv;
}());
export { Cv };
//# sourceMappingURL=Cv.js.map